import { useEffect, useMemo, useState, ComponentProps } from 'react';
import { connect } from 'react-redux';
import { inject, observer } from 'mobx-react';
import { UserProfile } from 'modules/auth/store/userAPI';

import { PartnerStore, partnerActions } from '../../../partners/store/partnerStore';
import {
  corporateExecutorActions,
  corporateExecutorStore,
} from '../../../partners/store/corporateExecutors/corporateExecutorStore';
import { SelectInput } from '../forms';

type PartnerSelectorProps = ComponentProps<typeof SelectInput> & {
  user?: UserProfile
  partnerStore?: PartnerStore
  addPartnerId?: string
  addPartnerName?: string
  /**
   * Фильтр по услугам — массив (показываем партнёров, оказывающих услуги).
   * POST /v1/partner/find, слот кэша partnersList (бывший API PartnerSelectorV2).
   * При одновременном переданном serviceId приоритет у serviceIds.
   */
  serviceIds?: number[]
  /** Сахар для одного сервиса: serviceId → [serviceId]. GET /v1/partner?serviceId=. */
  serviceId?: number
  // заблокировать Selector, если пользователь не федерал
  disabledIfNoFederal?: boolean
  excludedValues?: unknown[]
  dontShowCorporateClients?: boolean
  // показываем только корпоративных клиентов (партнёров)
  onlyCorporateClient?: boolean,
}

/**
 * Единый селектор партнёров (поглотил PartnerSelectorV2, этап 3 SELECTORS_TSX_PLAN, 2026-09-18).
 *
 * Две ветки загрузки — по пропсу фильтра, эндпоинты сохранены как были у исходников:
 *  • `serviceIds` (массив) → POST /v1/partner/find, слот `partnersList` (так работал V2);
 *  • `serviceId` / без фильтра → GET /v1/partner, слот `partnersListByService` (так работал V1).
 * Слияние слотов на один эндпоинт отложено: эквивалентность GET и POST /find по коду
 * не проверить (нет доступа к бэку) — см. SELECTORS_TSX_PLAN §1.2.
 * Гейт кэша и single-flight — внутри акшенов стора (partnerStore.ts).
 */
const PartnerSelector = (props: PartnerSelectorProps) => {
  const {
    user, partnerStore, addPartnerId, excludedValues,
    addPartnerName, serviceId, serviceIds, disabled, disabledIfNoFederal = false,
    dontShowCorporateClients = false, // не показывать корпоративных клиентов
    onlyCorporateClient, // ветка корп. клиентов — corporateExecutorStore (MobX)
    name = 'partnerId', sort, ...rest
  } = props;

  const isMultiServiceRequest = serviceIds !== undefined;
  // нормализованный ключ кэша GET-ветки: serviceId -> [serviceId], нет фильтра -> []
  const serviceIdKey = useMemo(() => (serviceId !== undefined && serviceId !== null ? [serviceId] : []), [serviceId]);

  const {
    partnersList, loadingPartnersList,
    partnersListByService, loadingPartnersListByService,
  } = partnerStore;

  // корп. клиенты — из стора привязок корп. клиентов (бывш. Redux corporateExecutors-слайс)
  const corporateClients = corporateExecutorStore.corporateClients;

  const [corporateLoading, setCorporateLoading] = useState(false);
  const shouldDisabled = !!user.partner?.id && disabledIfNoFederal;

  useEffect(() => {
    if (onlyCorporateClient) {
      setCorporateLoading(true);
      // кэш на сессию внутри стора: повторные mount'ы селектора не дублируют запрос
      corporateExecutorActions.getCorporateClients().finally(() => setCorporateLoading(false));
      return;
    }
    // serviceIds в депс — намеренно по identity: массив-литерал из рендера потребителя
    // даёт новую ссылку, повторные вызовы гасятся гейтом стора (isEqual) — так же работал V2
    if (isMultiServiceRequest) {
      partnerActions.getPartnerListByArray(serviceIds ?? []);
    } else {
      partnerActions.getPartnersListByService(serviceIdKey);
    }
  }, [serviceId, serviceIdKey, serviceIds, isMultiServiceRequest, onlyCorporateClient]);

  const loading = onlyCorporateClient
    ? corporateLoading
    : (isMultiServiceRequest ? loadingPartnersList : loadingPartnersListByService);

  const baseList = onlyCorporateClient
    ? corporateClients
    : (isMultiServiceRequest ? partnersList : partnersListByService);

  const partnersArray = useMemo(() => {
    const basePartnerList = baseList ?? [];
    const filteredPartners1 = basePartnerList.filter(v => (!(excludedValues ?? []).includes(v.id)));
    const filteredPartners = dontShowCorporateClients
      ? filteredPartners1.filter(v => !v.isCorporateClient)
      : filteredPartners1;
    if (filteredPartners.find(v => v.id === addPartnerId)) return filteredPartners;
    if (!addPartnerId) return filteredPartners;
    return filteredPartners.concat([{ id: addPartnerId, name: addPartnerName }]);
  }, [baseList, excludedValues, dontShowCorporateClients, addPartnerId, addPartnerName]);

  return (
    <SelectInput
      { ...rest }
      name={ name }
      data={ partnersArray }
      dataTitle='name'
      dataValue='id'
      loading={ loading }
      sort = { sort ?? true }
      value={ addPartnerId ?? user.partner?.id }
      disabled={ disabled || shouldDisabled }
    />
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.auth?.profile,
  };
};

export default connect(mapStateToProps)(inject('partnerStore')(observer(PartnerSelector)));
